import * as THREE from "three";
import {
  TRACK_KINDS,
  terrainHeightAt,
  trackTtlSec,
  type MapDef,
  type MapTerrain,
  type TrackSnap,
  type Weather,
} from "@rietz/shared";

/**
 * Footprints and blood on the ground, as ONE instanced draw call costing a
 * single uniform write per frame. The server owns the marks
 * (`shared/sim/tracks.ts`, `server/raid/tracks.ts`) and tells each client
 * about the ones near it once, with their age; this file stamps them and never
 * invents any.
 *
 * The fading is on the GPU: each instance carries three floats that never
 * change (born, ttl, kind) and the fragment shader works the rest out from one
 * clock uniform, so `update` is an assignment and `add` touches four attributes
 * at one index. A RING of `TRACK_CAPACITY` instances, oldest overwritten, no
 * compaction; an expired instance discards itself in the shader. Each mark is
 * a flat quad with a polygon offset, tilted to the terrain when on it and flat
 * on a slab — not a decal projector.
 */

/**
 * Marks alive at once. 4096 × 19 floats is a 300 KB static buffer, uploaded
 * only when it changes.
 */
export const TRACK_CAPACITY = 4096;

/**
 * Lifted off the ground by this, metres, on top of the polygon offset — for
 * ground the interpolation samples slightly differently from the collision that
 * laid it.
 */
const LIFT_M = 0.015;

/**
 * A footprint: 30 cm along the line of travel, 12 cm across — a little over a
 * real boot, so it reads at twenty metres.
 */
const PRINT_LENGTH_M = 0.3;
const PRINT_WIDTH_M = 0.12;

/**
 * A drop of blood, splash to small pool. Varied off the mark's id, not
 * randomly: the same id must draw the same on a reconnect.
 */
const BLOOD_MIN_M = 0.16;
const BLOOD_SPREAD_M = 0.14;
/** ...and spun by this much per id, in radians. An irrational-ish turn per
 *  mark, so a stream of drops down a corridor is not the same stamp repeated. */
const BLOOD_SPIN_RAD = 2.399;

/**
 * How close to the terrain a mark must be before it is tilted to the slope.
 * Above this it is on a slab, deck or stair — all flat.
 */
const TILT_SNAP_M = 0.3;
/**
 * Finite-difference arm for the terrain normal, metres — about the print's own
 * length, so it reads the slope rather than the heightfield's facets.
 */
const NORMAL_EPS_M = 0.3;

/** Atlas: two square tiles side by side, u ∈ [0, 0.5) sole and [0.5, 1) splat. */
const TILE_PX = 128;

/**
 * A print in SNOW is a hole: a cool blue-grey shadow against white, hence the
 * low alpha — darker reads as spilt paint.
 */
const SNOW_PRINT_RGBA: readonly [number, number, number, number] = [0.55, 0.62, 0.72, 0.55];
/**
 * ...and in mud, wet earth turned up onto a darker surface. Rain, and the
 * weathers that lay no prints and never ask.
 */
const MUD_PRINT_RGBA: readonly [number, number, number, number] = [0.24, 0.19, 0.14, 0.5];

const VERTEX_SHADER = /* glsl */ `
attribute float aBorn;
attribute float aTtl;
attribute float aKind;

varying vec2 vUv;
varying float vKind;
varying float vBorn;
varying float vTtl;

#include <fog_pars_vertex>

void main() {
  vUv = uv;
  vKind = aKind;
  vBorn = aBorn;
  vTtl = aTtl;
  vec4 mvPosition = modelViewMatrix * instanceMatrix * vec4(position, 1.0);
  #include <fog_vertex>
  gl_Position = projectionMatrix * mvPosition;
}
`;

const FRAGMENT_SHADER = /* glsl */ `
uniform float uTime;
uniform sampler2D uMap;
uniform vec4 uPrintColor;

varying vec2 vUv;
varying float vKind;
varying float vBorn;
varying float vTtl;

#include <fog_pars_fragment>

/** Old blood on cold ground: nearly black, and the only saturated thing in the
 *  field. It does not change with the weather — that is the point of it. */
const vec3 BLOOD_COLOR = vec3(0.42, 0.04, 0.04);
const float BLOOD_ALPHA = 0.85;

void main() {
  // How far through its life this mark is. The server forgets it at exactly
  // 1.0, so drawing anything at or past that would be drawing a mark that is
  // not there — a player could follow a trail the man who left it has already
  // outlived.
  float life = (uTime - vBorn) / max(vTtl, 0.001);
  if (life >= 1.0) discard;
  // HOLD, THEN FADE. A print that started fading the moment it was laid would
  // make every fresh trail look old; a print that vanished at its ttl would
  // pop. Full strength for the first 55 %, gone over the rest.
  float fade = 1.0 - smoothstep(0.55, 1.0, clamp(life, 0.0, 1.0));

  float blood = step(1.5, vKind);
  // The right foot is the left one mirrored across the tile — one sole in the
  // atlas, two feet on the ground.
  float mirror = step(0.5, vKind) * (1.0 - blood);
  float u = mix(vUv.x, 1.0 - vUv.x, mirror);
  float texA = texture2D(uMap, vec2(u * 0.5 + blood * 0.5, vUv.y)).a;

  vec3 color = mix(uPrintColor.rgb, BLOOD_COLOR, blood);
  float alpha = mix(uPrintColor.a, BLOOD_ALPHA, blood);
  gl_FragColor = vec4(color, alpha * fade * texA);
  #include <fog_fragment>
}
`;

export interface TrackField {
  /** Add this to the scene. Removed and disposed by `dispose`. */
  mesh: THREE.InstancedMesh;
  /** Marks currently held, i.e. distinct ids in the ring. Never above
   *  `TRACK_CAPACITY`. */
  readonly size: number;
  /**
   * Stamps one mark. Idempotent by `TrackSnap.id` (a reconnect re-sends).
   * `nowSec` is the caller's clock; the same clock must reach `update`.
   */
  add(t: TrackSnap, nowSec: number): void;
  /** One frame: pushes the clock to the shader. Nothing else. */
  update(nowSec: number): void;
  dispose(): void;
}

// Scratch. `add` is not a per-frame path but it runs in bursts of dozens on a
// snapshot, and the basis below is three vectors' worth of garbage per mark.
const up = new THREE.Vector3();
const fwd = new THREE.Vector3();
const right = new THREE.Vector3();
const back = new THREE.Vector3();
const scale = new THREE.Vector3();
const mat4 = new THREE.Matrix4();

/**
 * Builds the field for one map. The MAP supplies the terrain the prints tilt
 * to, the WEATHER the lifetime and footprint colour — both fixed for a raid.
 */
export function createTrackField(map: MapDef, weather: Weather): TrackField {
  const terrain = map.terrain ?? null;

  // 1 × 1 in the XZ plane, facing up, so the instance matrices are a plain
  // basis:
  // local +X across the print, local −Z the way it points, texture v = 1 the
  // toe.
  const geo = new THREE.PlaneGeometry(1, 1);
  geo.rotateX(-Math.PI / 2);

  const aBorn = new THREE.InstancedBufferAttribute(new Float32Array(TRACK_CAPACITY), 1);
  const aTtl = new THREE.InstancedBufferAttribute(new Float32Array(TRACK_CAPACITY), 1);
  const aKind = new THREE.InstancedBufferAttribute(new Float32Array(TRACK_CAPACITY), 1);
  aBorn.setUsage(THREE.DynamicDrawUsage);
  aTtl.setUsage(THREE.DynamicDrawUsage);
  aKind.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute("aBorn", aBorn);
  geo.setAttribute("aTtl", aTtl);
  geo.setAttribute("aKind", aKind);

  const texture = trackAtlas();
  const print = weather === "snow" ? SNOW_PRINT_RGBA : MUD_PRINT_RGBA;

  // Fog uniforms merged in (and cloned): the renderer writes the scene's fog
  // into
  // THIS material's copy every frame. The texture is attached AFTER the merge —
  // `cloneUniforms` deep-clones a `Texture` into a copy nothing can dispose.
  const uniforms = THREE.UniformsUtils.merge([
    THREE.UniformsLib.fog,
    {
      uTime: { value: 0 },
      uPrintColor: { value: new THREE.Vector4(print[0], print[1], print[2], print[3]) },
    },
  ]) as Record<string, THREE.IUniform>;
  uniforms["uMap"] = { value: texture };
  // The merged object is a CLONE, so the handle has to come back out of it.
  const uTime = uniforms["uTime"] as THREE.IUniform<number>;

  const mat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: VERTEX_SHADER,
    fragmentShader: FRAGMENT_SHADER,
    transparent: true,
    // Depth TESTED (a print inside a house is hidden by the wall) but not
    // depth-WRITTEN: two overlapping prints must not fight.
    depthWrite: false,
    depthTest: true,
    // Coplanar with the floor by construction; the lift handles the metre-scale
    // disagreement, this the millimetre one.
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    // Faces up, and only up. Nobody sees the underside of a footprint.
    side: THREE.FrontSide,
    // Fogged like the ground it is painted on — an unfogged decal at 70 m would
    // be the sharpest thing on the horizon.
    fog: true,
  });

  const mesh = new THREE.InstancedMesh(geo, mat, TRACK_CAPACITY);
  mesh.name = "tracks";
  // Nothing is drawn until something is stamped. `InstancedMesh` starts at full
  // count, which would render 4096 unit quads stacked at the origin.
  mesh.count = 0;
  // The field spans the whole map; the geometry's own bounding sphere would
  // have
  // the culler drop it the moment the player looked off centre.
  mesh.frustumCulled = false;
  // After the opaque world (0) and before the precipitation (5): a print is on
  // the ground, and snow falls in front of it.
  mesh.renderOrder = 1;
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

  /** id → ring slot, so a repeat costs nothing and evicts nothing. */
  const slotOf = new Map<number, number>();
  /**
   * Which id each slot holds, so recycling one can forget it. A plain array: an
   * `Int32Array` would truncate a large id into a key that no longer matches,
   * leaking the map entry.
   */
  const idAt: (number | null)[] = new Array<number | null>(TRACK_CAPACITY).fill(null);
  let next = 0;

  return {
    mesh,
    get size(): number {
      return slotOf.size;
    },

    add(t: TrackSnap, nowSec: number): void {
      if (slotOf.has(t.id)) return;

      const slot = next;
      next = (next + 1) % TRACK_CAPACITY;
      const evicted = idAt[slot] ?? null;
      if (evicted !== null) slotOf.delete(evicted);
      idAt[slot] = t.id;
      slotOf.set(t.id, slot);

      const blood = t.k === "blood";
      // Laid `age` seconds ago, not now, or a print seen on arrival would
      // outlive the server's copy.
      aBorn.setX(slot, nowSec - t.age);
      aTtl.setX(slot, trackTtlSec(t.k, weather));
      aKind.setX(slot, TRACK_KINDS.indexOf(t.k));

      // Size and spin.
      let yaw = t.yaw;
      if (blood) {
        // `abs`: a negative modulus would scale the quad inside-out — invisible
        // on a single-sided material.
        const s = BLOOD_MIN_M + (BLOOD_SPREAD_M * (Math.abs(t.id * 7) % 5)) / 4;
        scale.set(s, 1, s);
        yaw += t.id * BLOOD_SPIN_RAD;
      } else {
        scale.set(PRINT_WIDTH_M, 1, PRINT_LENGTH_M);
      }

      // The floor's normal, when the mark is lying on the terrain rather than
      // on a slab. `terrainNormal` leaves `up` at +Y when it is not.
      terrainNormal(terrain, t.pos.x, t.pos.y, t.pos.z, up);

      // A basis with `up` for up and the direction of travel for local −Z — the
      // same (−sin, 0, −cos) the camera and every avatar use.
      fwd.set(-Math.sin(yaw), 0, -Math.cos(yaw));
      // Projected onto the ground plane, so a print on a slope points along the
      // hill; a degenerate result leaves the flat forward.
      fwd.addScaledVector(up, -fwd.dot(up));
      if (fwd.lengthSq() < 1e-8) fwd.set(-Math.sin(yaw), 0, -Math.cos(yaw));
      fwd.normalize();
      back.copy(fwd).negate();
      right.copy(up).cross(back).normalize();

      mat4.makeBasis(right, up, back);
      mat4.scale(scale);
      mat4.setPosition(t.pos.x, t.pos.y + LIFT_M, t.pos.z);
      mesh.setMatrixAt(slot, mat4);

      // The high-water mark, never a count of live marks: the ring fills once
      // and then stays full, and an expired slot discards itself in the shader.
      if (slot >= mesh.count) mesh.count = slot + 1;

      mesh.instanceMatrix.needsUpdate = true;
      aBorn.needsUpdate = true;
      aTtl.needsUpdate = true;
      aKind.needsUpdate = true;
    },

    update(nowSec: number): void {
      // The entire per-frame cost of the feature.
      uTime.value = nowSec;
    },

    dispose(): void {
      mesh.removeFromParent();
      mesh.dispose();
      geo.dispose();
      mat.dispose();
      texture?.dispose();
      slotOf.clear();
    },
  };
}

// ---------------------------------------------------------------------------

/**
 * The terrain's normal at `(x, z)` into `out`, or +Y when the mark is not on
 * the terrain: half a metre above the heightfield is a slab, bridge or stair,
 * all flat.
 */
function terrainNormal(
  terrain: MapTerrain | null,
  x: number,
  y: number,
  z: number,
  out: THREE.Vector3,
): void {
  out.set(0, 1, 0);
  if (terrain === null) return;
  const h = terrainHeightAt(terrain, x, z);
  if (h === null || Math.abs(y - h) > TILT_SNAP_M) return;
  const e = NORMAL_EPS_M;
  const hxl = terrainHeightAt(terrain, x - e, z);
  const hxr = terrainHeightAt(terrain, x + e, z);
  const hzl = terrainHeightAt(terrain, x, z - e);
  const hzr = terrainHeightAt(terrain, x, z + e);
  // Any sample off the field or over a cellar hole: keep the flat normal rather
  // than invent a slope from half a difference.
  if (hxl === null || hxr === null || hzl === null || hzr === null) return;
  out.set(-(hxr - hxl) / (2 * e), 1, -(hzr - hzl) / (2 * e)).normalize();
}

/**
 * The two-tile alpha atlas, painted once per field. `null` without a `document`
 * (headless tests build the field and never draw it). ALPHA ONLY — colour comes
 * from the shader, so one texture serves snow, mud and blood.
 */
function trackAtlas(): THREE.CanvasTexture | null {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = TILE_PX * 2;
  canvas.height = TILE_PX;
  const ctx = canvas.getContext("2d");
  if (ctx === null) return null;

  ctx.fillStyle = "#ffffff";
  ctx.strokeStyle = "rgba(255, 255, 255, 0.45)";
  // A 4 px stroke straddling the fill's edge is a 2 px feather — cheaper and
  // more portable than a canvas blur.
  ctx.lineWidth = 4;

  // --- Tile 0: a boot sole. Toe at the TOP (`CanvasTexture` flips Y; v = 1 is
  // the
  // leading edge). Sole and heel are separate blobs — the arch is what reads as
  // a boot.
  drawEllipse(ctx, TILE_PX * 0.5, TILE_PX * 0.32, TILE_PX * 0.2, TILE_PX * 0.26);
  drawEllipse(ctx, TILE_PX * 0.5, TILE_PX * 0.82, TILE_PX * 0.17, TILE_PX * 0.14);

  // --- Tile 1: a splat. Fixed offsets, not random — an atlas that differed
  // between two clients would have them looking at different blood.
  const cx = TILE_PX * 1.5;
  const cy = TILE_PX * 0.5;
  drawEllipse(ctx, cx, cy, TILE_PX * 0.22, TILE_PX * 0.2);
  const spots: readonly [number, number, number][] = [
    [0.3, -0.28, 0.08],
    [-0.34, 0.16, 0.07],
    [0.14, 0.33, 0.06],
    [-0.2, -0.3, 0.05],
    [0.36, 0.12, 0.045],
  ];
  for (const [dx, dy, r] of spots) {
    drawEllipse(ctx, cx + dx * TILE_PX, cy + dy * TILE_PX, r * TILE_PX, r * TILE_PX);
  }

  const tex = new THREE.CanvasTexture(canvas);
  // No colour space: the atlas is a mask. Linear filtering — a print is never
  // on-axis or at native size.
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}

/** One soft-edged blob: filled, then stroked to feather the boundary. */
function drawEllipse(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  rx: number,
  ry: number,
): void {
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
}
